import React from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from 'react-bootstrap';
import { useStateValue } from '../context/state.jsx';
import { setShowAddProjectModal } from '../context/datasets.js';
import { closeOpenProject, focusProject, openInSession, sourceFormatLabel } from '../util/openProject.js';

const statusForProject = (datasetId, openIds, focusedId) => {
	if (String(focusedId) === String(datasetId)) {
		return { label: 'Focused', className: 'statusBadge-focused' };
	}
	if (openIds.includes(String(datasetId))) {
		return { label: 'Open', className: 'statusBadge-open' };
	}
	return { label: 'Not open', className: 'statusBadge-closed' };
};

const ProjectRow = ({
	datasetId,
	dataset,
	status,
	showOpen,
	showFocus,
	showClose,
	onOpen,
	onFocus,
	onClose
}) => (
	<li className="setupProjectRow">
		<div>
			<div className="setupProjectRowMeta">
				<strong>{dataset.Name}</strong>
				<span className="sourceFormatBadge">{sourceFormatLabel(dataset)}</span>
				<span className={`statusBadge ${status.className}`}>{status.label}</span>
			</div>
			<div className="setupProjectRowSub">
				{dataset.Owned ? 'Owned by you' : `Shared by ${dataset.Owner || 'another user'}`}
			</div>
		</div>
		<div className="setupProjectRowActions">
			{showFocus
				? <Button bsSize="small" onClick={onFocus}>Focus</Button>
				: null}
			{showOpen
				? <Button bsSize="small" bsStyle="primary" onClick={onOpen}>Open</Button>
				: null}
			<Link className="btn btn-default btn-sm" to={`/setup/projects/${datasetId}`}>Configure</Link>
			{showClose
				? <Button bsSize="small" onClick={onClose}>Close</Button>
				: null}
		</div>
	</li>
);

ProjectRow.propTypes = {
	datasetId: PropTypes.string.isRequired,
	dataset: PropTypes.object.isRequired,
	status: PropTypes.object.isRequired,
	showOpen: PropTypes.bool,
	showFocus: PropTypes.bool,
	showClose: PropTypes.bool,
	onOpen: PropTypes.func,
	onFocus: PropTypes.func,
	onClose: PropTypes.func
};

const ProjectSetup = ({ datasetsLoading }) => {
	const [{ datasets, openDatasets, currentDataset }, dispatch] = useStateValue();
	const navigate = useNavigate();
	const datasetMap = datasets && typeof datasets === 'object' && !Array.isArray(datasets) ? datasets : {};
	const datasetIds = Object.keys(datasetMap);
	const focusedId = currentDataset || null;
	const openIds = (openDatasets || []).map(String).filter(id => datasetMap[id]);
	const libraryIds = datasetIds;

	const addToSession = datasetId => {
		openInSession({ dispatch, datasetId });
	};

	const openOrFocus = datasetId => {
		focusProject({ dispatch, navigate, datasetId, datasets: datasetMap });
	};

	const closeProject = datasetId => {
		closeOpenProject({
			dispatch,
			navigate,
			datasetId,
			openDatasets: openIds,
			focusedDatasetId: focusedId,
			datasets: datasetMap,
			redirectIfFocused: false
		});
	};

	if (!datasetIds.length) {
		return (
			<div className="paddedTabContent setupProjectsPane">
				<div className="setupSectionHeader">
					<div>
						<h2>Project configuration</h2>
						<p>Open several projects at once, then configure each from its hub.</p>
					</div>
					<Button
						bsStyle="primary"
						className="setupPrimaryBtn"
						onClick={() => dispatch(setShowAddProjectModal(true))}>
						<span className="glyphicon glyphicon-cloud leftGlyph"></span>
						Add Project
					</Button>
				</div>
				<div className="setupEmptyState">
					<p>
						{datasetsLoading
							? 'Loading projects…'
							: 'No projects are loaded yet. Add a project or open one from the main app, then return here.'}
					</p>
				</div>
			</div>
		);
	}

	return (
		<div className="paddedTabContent setupProjectsPane">
			<div className="setupSectionHeader">
				<div>
					<h2>Project configuration</h2>
					<p>Open adds to the session. Configure opens the project hub without replacing other open projects.</p>
				</div>
				<Button
					bsStyle="primary"
					className="setupPrimaryBtn"
					onClick={() => dispatch(setShowAddProjectModal(true))}>
					<span className="glyphicon glyphicon-cloud leftGlyph"></span>
					Add Project
				</Button>
			</div>

			<section className="setupCard setupProjectListCard">
				<div className="setupCardHeader">
					<div>
						<h3>Open in this session</h3>
						<p>Loaded for map / list — switch focus without closing the others.</p>
					</div>
					<Link className="btn btn-default btn-sm" to="/app/">View in app</Link>
				</div>
				{openIds.length
					? (
						<ul className="setupProjectList">
							{openIds.map(datasetId => (
								<ProjectRow
									key={datasetId}
									datasetId={datasetId}
									dataset={datasetMap[datasetId]}
									status={statusForProject(datasetId, openIds, focusedId)}
									showFocus={String(focusedId) !== String(datasetId)}
									showClose
									onFocus={() => openOrFocus(datasetId)}
									onClose={() => closeProject(datasetId)} />
							))}
						</ul>
					)
					: (
						<div className="setupEmptyState">
							<p>No projects are open. Open one from the library below.</p>
						</div>
					)}
			</section>

			<section className="setupCard setupProjectListCard">
				<div className="setupCardHeader">
					<div>
						<h3>All projects</h3>
						<p>Library of projects you can access. Configure never requires a project to be open.</p>
					</div>
				</div>
				<ul className="setupProjectList">
					{libraryIds.map(datasetId => {
						const isOpen = openIds.includes(String(datasetId));
						const isFocused = String(focusedId) === String(datasetId);
						return (
							<ProjectRow
								key={datasetId}
								datasetId={datasetId}
								dataset={datasetMap[datasetId]}
								status={statusForProject(datasetId, openIds, focusedId)}
								showOpen={!isOpen}
								showFocus={isOpen && !isFocused}
								onOpen={() => addToSession(datasetId)}
								onFocus={() => openOrFocus(datasetId)} />
						);
					})}
				</ul>
			</section>
		</div>
	);
};

ProjectSetup.propTypes = {
	datasetsLoading: PropTypes.bool
};

export default ProjectSetup;
